import * as XLSX from "xlsx";

/**
 * Validation for the claims-based (UW_Method__c = Claims) census upload.
 *
 * Unlike the standard census (parseCensusFile), a claims census is NEVER parsed
 * into employee records — we only confirm it's the right template and that it
 * carries at least one member, then attach the file as-is. The shipped template
 * ("Final Enrollment Census File.xlsx") has two tabs; data lives on the
 * "Enrollment File" tab with the header row at row 4 (a Required/Optional
 * annotation row sits directly beneath it, and data starts the row after that).
 */

// Tab that holds the records to validate (per the story AC).
const ENROLLMENT_TAB = "Enrollment File";

// Distinctive, stable headers from the Enrollment File tab's header row.
// Subset match (not every column), tolerant of trailing-space/casing drift
// and unrelated header relabels, consistent with the standard census matcher.
// Since nothing is parsed, this only needs to confirm the right template. It
// accepts both the 36-column template and the AB#3619 template that appends
// Division Billing Code and Description at AK/AL.
const REQUIRED_CLAIMS_HEADERS = [
  "group name",
  "member first name",
  "member last name",
  "member birth date",
  "relationship",
  "enrollment status",
];

// Cell values that mark non-data rows: the "Field" header label, the
// Required/Optional annotation row (incl. the template's "Requird" typo), and
// the "Entry" row label that precedes the first member on a blank template.
const NON_DATA_TOKENS = new Set(["field", "required", "optional", "requird", "entry"]);

const MISMATCH_MESSAGE =
  "File content does not match the downloadable template. Please download and use the census template above.";
const NO_RECORDS_MESSAGE =
  "No records found in the uploaded file. Please add employee data to the template and re-upload.";
const UNREADABLE_MESSAGE =
  "Unable to read file. Please ensure it's a valid Excel file and not password-protected.";

export interface ClaimsCensusValidationResult {
  success: boolean;
  errorMessage: string | null;
}

export function validateClaimsCensusFile(buffer: ArrayBuffer): ClaimsCensusValidationResult {
  // CVE-2023-30533: xlsx@0.18.5 prototype-pollution — mitigated here by never
  // spreading raw rows; we only read individual cell values by coordinate.
  let workbook: XLSX.WorkBook;
  try {
    workbook = XLSX.read(buffer, { type: "array" });
  } catch {
    return { success: false, errorMessage: UNREADABLE_MESSAGE };
  }

  const sheetName = workbook.SheetNames.find(
    (n) => n.trim().toLowerCase() === ENROLLMENT_TAB.toLowerCase(),
  );
  if (!sheetName) return { success: false, errorMessage: MISMATCH_MESSAGE };

  const sheet = workbook.Sheets[sheetName];
  const range = XLSX.utils.decode_range(sheet["!ref"] ?? "A1");

  // Locate the header row — the row whose first column is "Field" (row 4 in the
  // shipped template; scan the first several rows to tolerate minor shifts).
  let headerRow = -1;
  for (let r = range.s.r; r <= Math.min(range.s.r + 8, range.e.r); r++) {
    const cell = sheet[XLSX.utils.encode_cell({ r, c: range.s.c })];
    if (cell?.v && String(cell.v).trim().toLowerCase() === "field") {
      headerRow = r;
      break;
    }
  }
  if (headerRow < 0) return { success: false, errorMessage: MISMATCH_MESSAGE };

  const headers: string[] = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    const cell = sheet[XLSX.utils.encode_cell({ r: headerRow, c })];
    headers.push(cell?.v ? String(cell.v).trim().toLowerCase() : "");
  }

  const missing = REQUIRED_CLAIMS_HEADERS.filter(
    (h) => !headers.some((fh) => fh.includes(h)),
  );
  if (missing.length > 0) return { success: false, errorMessage: MISMATCH_MESSAGE };

  // A "record" is a row below the header that carries member data in a column
  // past the row-label column (col A). The Required/Optional annotation row and
  // the "Entry"-only label row contain only NON_DATA_TOKENS, so they're skipped.
  let hasRecords = false;
  for (let r = headerRow + 1; r <= range.e.r && !hasRecords; r++) {
    for (let c = range.s.c + 1; c <= range.e.c; c++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      const raw = cell?.v;
      if (raw === undefined || raw === null) continue;
      const val = String(raw).trim();
      if (val === "" || NON_DATA_TOKENS.has(val.toLowerCase())) continue;
      hasRecords = true;
      break;
    }
  }
  if (!hasRecords) return { success: false, errorMessage: NO_RECORDS_MESSAGE };

  return { success: true, errorMessage: null };
}
